import { CURRENT_CLIENT_VERSION } from '../clientVersion.ts'
import { resolveWebAppUrl } from '../api/ZionServer.ts'
import { t, tf } from '../i18n/index.ts'

function compareSemver(a: string, b: string): number {
  const parse = (v: string) =>
    v
      .replace(/^v/i, '')
      .trim()
      .split('.')
      .map((part) => parseInt(part, 10) || 0)
  const pa = parse(a)
  const pb = parse(b)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (diff !== 0) return diff
  }
  return 0
}

async function fetchRequiredVersion(): Promise<string | null> {
  try {
    const ctrl = new AbortController()
    const timer = window.setTimeout(() => ctrl.abort(), 3000)
    const res = await fetch(resolveWebAppUrl('/api/version'), { cache: 'no-store', signal: ctrl.signal })
    window.clearTimeout(timer)
    if (!res.ok) return null
    const data = (await res.json()) as { version?: string }
    const version = String(data.version ?? '').trim()
    return version || null
  } catch {
    return null
  }
}

function showForceUpdateOverlay(requiredVersion: string): void {
  const overlay = document.getElementById('force-update-overlay')
  if (!overlay) return

  const titleEl = document.getElementById('force-update-title')
  const descEl = document.getElementById('force-update-desc')
  const versionsEl = document.getElementById('force-update-current')
  const btn = document.getElementById('force-update-btn')
  if (titleEl) titleEl.textContent = t('force_update_title')
  if (descEl) descEl.textContent = t('force_update_desc')
  if (versionsEl) {
    versionsEl.textContent = tf('force_update_versions', {
      current: CURRENT_CLIENT_VERSION,
      required: requiredVersion,
    })
  }
  if (btn) btn.textContent = t('force_update_btn')

  overlay.classList.remove('hidden')
  overlay.setAttribute('aria-hidden', 'false')
  document.body.classList.add('force-update-locked')
}

function bindForceUpdateButton(): void {
  const btn = document.getElementById('force-update-btn')
  if (!btn || btn.dataset.bound === '1') return
  btn.dataset.bound = '1'

  btn.addEventListener('click', () => {
    if ('caches' in window) {
      void caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (const registration of registrations) {
          registration.update()
        }
      })
    }
    setTimeout(() => {
      ;(window.location.reload as (forced?: boolean) => void)(true)
    }, 800)
  })
}

/** true = UI bloccata finché l'utente non aggiorna. */
export async function enforceMinimumClientVersion(): Promise<boolean> {
  bindForceUpdateButton()
  const requiredVersion = await fetchRequiredVersion()
  if (!requiredVersion) return false
  if (compareSemver(CURRENT_CLIENT_VERSION, requiredVersion) < 0) {
    showForceUpdateOverlay(requiredVersion)
    return true
  }
  return false
}
